import React, { useEffect, useState } from 'react';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Modal } from './Modal';
import { Button, ButtonVariant } from './Button';

type ConfirmState = 'idle' | 'pending' | 'success' | 'error';

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  title: string;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Visual weight of the confirm button — 'danger' for destructive actions */
  variant?: Extract<ButtonVariant, 'danger' | 'primary'>;
  successMessage?: string;
}

/**
 * Confirmation modal whose confirm button runs an async action in place:
 * spinner while pending, then an inline success/error result instead of
 * closing immediately, so the user sees the outcome before the dialog goes away.
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger',
  successMessage = 'Done',
}) => {
  const [state, setState] = useState<ConfirmState>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (isOpen) {
      setState('idle');
      setErrorMessage('');
    }
  }, [isOpen]);

  const handleConfirm = async () => {
    setState('pending');
    try {
      await onConfirm();
      setState('success');
      setTimeout(onClose, 1200);
    } catch (error) {
      setState('error');
      setErrorMessage(error instanceof Error ? error.message : 'Something went wrong');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={state === 'idle' || state === 'pending' ? title : undefined} size="sm">
      {(state === 'idle' || state === 'pending') && (
        <>
          {description && <div className="text-sm text-gray-600 dark:text-gray-400">{description}</div>}
          <div className="mt-6 flex justify-end gap-3">
            <Button variant="outline" size="sm" onClick={onClose} disabled={state === 'pending'}>
              {cancelLabel}
            </Button>
            <Button variant={variant} size="sm" onClick={handleConfirm} loading={state === 'pending'}>
              {confirmLabel}
            </Button>
          </div>
        </>
      )}

      {state === 'success' && (
        <div className="flex flex-col items-center text-center py-4">
          <CheckCircleIcon className="h-10 w-10 text-green-500" />
          <p className="mt-3 text-sm font-medium text-gray-900 dark:text-white">{successMessage}</p>
        </div>
      )}

      {state === 'error' && (
        <div className="flex flex-col items-center text-center py-4">
          <ExclamationTriangleIcon className="h-10 w-10 text-danger-500" />
          <p className="mt-3 text-sm font-medium text-gray-900 dark:text-white">{errorMessage}</p>
          <div className="mt-4 flex gap-3">
            <Button variant="outline" size="sm" onClick={onClose}>
              Close
            </Button>
            <Button variant={variant} size="sm" onClick={handleConfirm}>
              Retry
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};
